import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Wind, Play, Pause } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";

// 4-4-6-2 breathing (Box-ish, calming)
const PHASES = [
    { name: "Breathe in", duration: 4, scale: 1.4 },
    { name: "Hold", duration: 4, scale: 1.4 },
    { name: "Breathe out", duration: 6, scale: 0.85 },
    { name: "Rest", duration: 2, scale: 0.85 },
];

export default function Breathing() {
    const [running, setRunning] = useState(false);
    const [phaseIdx, setPhaseIdx] = useState(0);
    const [remaining, setRemaining] = useState(PHASES[0].duration);
    const [totalSec, setTotalSec] = useState(0);
    const timerRef = useRef(null);
    const reduced = useReducedMotion();

    useEffect(() => {
        if (!running) return;
        timerRef.current = setInterval(() => {
            setRemaining(r => {
                if (r > 1) return r - 1;
                setPhaseIdx(i => (i + 1) % PHASES.length);
                return null; // will be reset by effect below
            });
            setTotalSec(t => t + 1);
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [running]);

    useEffect(() => {
        setRemaining(PHASES[phaseIdx].duration);
    }, [phaseIdx]);

    const toggle = () => {
        if (running) {
            setRunning(false);
        } else {
            setPhaseIdx(0);
            setRemaining(PHASES[0].duration);
            setTotalSec(0);
            setRunning(true);
        }
    };

    const phase = PHASES[phaseIdx];

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 sm:p-10 relative overflow-hidden">
            <div className="blob blob-peach w-[600px] h-[600px] -top-40 -left-40" />
            <div className="blob blob-cream w-[500px] h-[500px] -bottom-40 -right-40" />

            <div className="relative z-10 text-center max-w-lg">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-xl border border-border shadow-soft mb-8">
                    <Wind className="w-4 h-4 text-primary" strokeWidth={1.5} />
                    <span className="text-sm font-medium">Slow. Gentle. On your own pace.</span>
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight mb-3">Breathe with me.</h1>
                <p className="text-muted-foreground mb-16">Four in, hold, six out, rest. Repeat as long as feels good.</p>

                <div className="relative flex items-center justify-center h-80 mb-10">
                    <motion.div
                        animate={{ scale: running ? phase.scale : 1 }}
                        transition={{ duration: running ? phase.duration : 0.6, ease: "easeInOut" }}
                        className="absolute w-56 h-56 rounded-full bg-primary/25 blur-2xl"
                    />
                    <motion.div
                        animate={reduced ? {} : { scale: running ? phase.scale : 1 }}
                        transition={{ duration: running ? phase.duration : 0.6, ease: "easeInOut" }}
                        className="w-56 h-56 rounded-full bg-gradient-to-br from-primary via-primary/80 to-accent flex items-center justify-center shadow-soft-lg"
                        data-testid="breathing-circle"
                    >
                        <div className="text-center text-primary-foreground">
                            <div className="font-heading text-3xl font-[700] tracking-tight mb-1">{running ? phase.name : "Ready?"}</div>
                            {running && <div className="text-primary-foreground/80 text-lg font-medium">{remaining}s</div>}
                        </div>
                    </motion.div>
                </div>

                <Button
                    onClick={toggle}
                    size="lg"
                    className="rounded-full h-14 px-10 text-base font-medium shadow-soft-lg"
                    data-testid="breathing-toggle-btn"
                >
                    {running ? (<><Pause className="w-5 h-5 mr-2" strokeWidth={1.5} /> Pause</>) : (<><Play className="w-5 h-5 mr-2" strokeWidth={1.5} fill="currentColor" /> Start breathing</>)}
                </Button>

                {running && (
                    <p className="text-sm text-muted-foreground mt-6">{Math.floor(totalSec / 60)}m {totalSec % 60}s so far. Beautifully done.</p>
                )}
            </div>
            <div className="absolute bottom-0 left-0 right-0 z-10">
                <FeedbackFooter />
            </div>
        </div>
    );
}
